/**
 * ReceivedFormDialog — New / Edit / Copy for Virtual Warehouse › Received.
 * Converted 1:1 from Access `frmVirtualFactoryQryReceivedAdd` (title "Received").
 * Two-column grid; combos = flat Autocomplete "<CODE> — <NAME>" storing the code.
 *
 * PK = RDN No (rdnNo). access-suggest pattern: New/Copy prefill a suggested value
 * (suggest-receipt-no) + duplicate check (check-receipt-no) on blur/change; Edit
 * locks the field. Validation (verbatim Access): blank → "Please enter valid RDN
 * No!"; must start "RDN" → "Wrong RDN No format!"; duplicate → "RDN No existed!".
 *
 * Hydrate (Edit/Copy): the parent view has selectedRow, but props expose only
 * `recordId`. There is NO get-by-id endpoint, so we read the row from the SAME
 * cached list the view already loaded via `selectFromResult` + find(id). The list
 * query is keyed by `{page,pageSize,dateInFrom,dateInTo}`, so the dialog MUST pass
 * the SAME `dateInFrom`/`dateInTo` (from the page filter) to hit that cache entry —
 * otherwise the args differ, RTK Query misses the cache and refetches the whole
 * (up to 1M-row) list. Selection can only happen on a loaded row, so once the args
 * match the cache entry is guaranteed present.
 * A `formLoading` gate shows the standard spinner on Edit/Copy until the cached row
 * (and, for Copy, the suggested RDN No) is ready, so fields never flash blank.
 */
import { useEffect, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type LookupItem,
  type ReceivedRow,
  type ReceivedWrite,
  useCreateReceivedMutation,
  useUpdateReceivedMutation,
  useLazyGetReceivedSuggestReceiptNoQuery,
  useLazyGetReceivedCheckReceiptNoQuery,
  useLazyGetReceivedDoDerivedQuery,
  useGetReceivedListQuery,
  useVwCpLookupQuery,
  useVwDoNoLookupQuery,
  useVwWarehouseLookupQuery,
  useVwQualityLookupQuery,
} from '../../store/api/virtualWarehouseApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import { TEXT_TRUNCATE } from '@/theme/responsive'

type Mode = 'new' | 'edit' | 'copy'

interface Props {
  open: boolean
  mode: Mode
  recordId: number | null
  canEdit: boolean
  /** Page-filter date range — MUST match the parent list query args so the
   *  hydrate `selectFromResult` reads from the same cache entry (no refetch). */
  dateInFrom?: string | null
  dateInTo?: string | null
  onClose: () => void
  onSaved: () => void
}

// Must match the args the parent views use so we hit the same cache entry.
const ALL_ROWS_PAGE_SIZE = 1_000_000

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const sectionTitleSx: SxProps<Theme> = {
  fontWeight: 700, fontSize: '0.8125rem', color: 'primary.main',
  mt: 1.5, mb: 0.75,
}
const grid2Sx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
/** Access Format="Standard": 2 decimals + thousands sep, right-aligned. */
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))

/* ───────── Reusable flat combo (Autocomplete "<CODE> — <NAME>") ───────── */
function Combo(props: {
  label: string
  value: string
  valueLabel?: string
  options: LookupItem[]
  loading?: boolean
  disabled?: boolean
  /** Render the option label verbatim (name-primary combos where BE already
   *  formats the display, e.g. Access name-first / name-only). Default false
   *  keeps the house "<CODE> — <NAME>" format. */
  rawLabel?: boolean
  onInputChange?: (q: string) => void
  onPick: (value: string, item: LookupItem | null) => void
}) {
  const { label, value, valueLabel, options, loading, disabled, rawLabel, onInputChange, onPick } = props
  // Inject the current value INTO options (server-side lookups may not include a
  // saved value) so the chip shows on Edit even before/without a matching fetch.
  const sel = value ? options.find((o) => o.value === value) ?? { value, label: valueLabel || value } : null
  const opts = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  const getLabel = (o: LookupItem) =>
    !o ? '' : rawLabel ? String(o.label ?? '') : o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`
  return (
    <Autocomplete
      size={SM}
      disabled={disabled}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => onPick(v?.value ?? '', v)}
      onInputChange={onInputChange ? (_e, v, reason) => { if (reason === 'input') onInputChange(v) } : undefined}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={onInputChange ? (x) => x : undefined}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  )
}

/* Grades (Access order) + display labels. underS12 → "<S12". */
const GRADE_KEYS = [
  'mc', 'bb', 'brown', 'cherry', 'overdry', 'fm', 'mouldy', 'white', 'black', 'brokens',
  'foreign', 'insect', 'immature', 'old', 's18', 's17', 's16', 's15', 's14', 's13', 's12', 'underS12',
] as const
// Access 2-column interleave: col1 top→bottom, col2 top→bottom.
const GRADE_COL1 = ['mc', 'bb', 'brown', 'cherry', 'overdry', 'fm', 'mouldy', 'white', 'black', 'brokens', 'foreign'] as const
const GRADE_COL2 = ['insect', 'immature', 'old', 's18', 's17', 's16', 's15', 's14', 's13', 's12', 'underS12'] as const
const GRADE_LABELS: Record<string, string> = {
  mc: 'MC', bb: 'BB', brown: 'Brown', cherry: 'Cherry', overdry: 'Overdry', fm: 'FM',
  mouldy: 'Mouldy', white: 'White', black: 'Black', brokens: 'Brokens', foreign: 'Foreign',
  insect: 'Insect', immature: 'Immature', old: 'Old', s18: 'S18', s17: 'S17', s16: 'S16',
  s15: 'S15', s14: 'S14', s13: 'S13', s12: 'S12', underS12: '<S12',
}

type Draft = Record<string, string>
const EMPTY: Draft = (() => {
  const d: Draft = {
    receiptNo: '', dateIn: '', truckNo: '', supplierCode: '', clientCode: '', warehouseCode: '',
    qualityCode: '', announcedQuantity: '', doNo: '', noOfBag: '', vgmQuantity: '',
    deductionKg: '', deductionVnd: '', deductionOther: '', note: '',
    // Read-only displays (not saved):
    paymentQuantity: '', doSaleContract: '', doPackaging: '', doQuality: '', doNetWeight: '',
    supplierCodeRo: '', clientCodeRo: '',
  }
  GRADE_KEYS.forEach((g) => { d[g] = '' })
  return d
})()

export default function ReceivedFormDialog({ open, mode, recordId, canEdit, dateInFrom, dateInTo, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [labels, setLabels] = useState<Record<string, string>>({})
  const [rdnExists, setRdnExists] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const set = (k: string, v: string) => setDraft((p) => ({ ...p, [k]: v }))

  // Hydrate source: read the selected row from the parent's cached list. Args MUST
  // mirror the parent query (incl. dateInFrom/dateInTo) to hit the same cache entry.
  const { row } = useGetReceivedListQuery(
    { page: 1, pageSize: ALL_ROWS_PAGE_SIZE, dateInFrom, dateInTo },
    {
      skip: !open || mode === 'new' || recordId == null,
      selectFromResult: ({ data }) => ({ row: data?.items.find((r) => r.id === recordId) as ReceivedRow | undefined }),
    },
  )

  const [suggestReceiptNo] = useLazyGetReceivedSuggestReceiptNoQuery()
  const [checkReceiptNo] = useLazyGetReceivedCheckReceiptNoQuery()
  const [fetchDoDerived] = useLazyGetReceivedDoDerivedQuery()
  const [createReceived, { isLoading: creating }] = useCreateReceivedMutation()
  const [updateReceived, { isLoading: updating }] = useUpdateReceivedMutation()
  const saving = creating || updating

  // Combo lookups (server-side search) — load when open.
  const skip = { skip: !open }
  const [supplierQ, setSupplierQ] = useState('')
  const [clientQ, setClientQ] = useState('')
  const [warehouseQ, setWarehouseQ] = useState('')
  const [qualityQ, setQualityQ] = useState('')
  const [doNoQ, setDoNoQ] = useState('')
  const suppliers = useVwCpLookupQuery(supplierQ || undefined, skip)
  const clients = useVwCpLookupQuery(clientQ || undefined, skip)
  const warehouses = useVwWarehouseLookupQuery(warehouseQ || undefined, skip)
  const qualities = useVwQualityLookupQuery(qualityQ || undefined, skip)
  const doNos = useVwDoNoLookupQuery(doNoQ || undefined, skip)

  const runCheck = (val: string) => {
    const v = val.trim()
    if (!v) { setRdnExists(false); return }
    void checkReceiptNo(v).unwrap().then((r) => setRdnExists(r.exists)).catch(() => setRdnExists(false))
  }

  // Populate the 4 read-only DO-derived displays.
  const applyDoDerived = (doNo: string) => {
    if (!doNo) { setDraft((p) => ({ ...p, doSaleContract: '', doPackaging: '', doQuality: '', doNetWeight: '' })); return }
    void fetchDoDerived(doNo).unwrap()
      .then((d) => setDraft((p) => ({
        ...p, doSaleContract: s(d.saleContract), doPackaging: s(d.packaging),
        doQuality: s(d.quality), doNetWeight: fmtStd(d.netWeightKg),
      })))
      .catch(() => undefined)
  }

  // Hydrate on open. Deps only [open, mode, recordId, row] (not the list object).
  // New renders immediately (no spinner); Edit/Copy gate on `formLoading` until the
  // cached row (and Copy's suggested RDN No) is ready so fields never flash blank.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setRdnExists(false)
    void (async () => {
      if (mode === 'new') {
        setFormLoading(false)
        const sug = await suggestReceiptNo().unwrap().catch(() => null)
        if (cancelled) return
        setLabels({})
        setDraft({ ...EMPTY, receiptNo: sug?.receiptNo ?? '', dateIn: dayjs().format('YYYY-MM-DD') })
        return
      }
      // edit / copy — hydrate from the cached row.
      setFormLoading(true)
      if (!row) return // wait for cache; effect re-runs when `row` arrives
      if (mode === 'copy') {
        const sug = await suggestReceiptNo().unwrap().catch(() => null)
        if (cancelled) return
        setLabels({ supplierCode: s(row.supplierName), clientCode: s(row.clientName), warehouseCode: s(row.warehouseName) })
        const next: Draft = { ...EMPTY }
        next.receiptNo = sug?.receiptNo ?? ''
        hydrateFromRow(next, row, true)
        setDraft(next)
        applyDoDerived(s(row.doNo))
      } else {
        setLabels({ supplierCode: s(row.supplierName), clientCode: s(row.clientName), warehouseCode: s(row.warehouseName) })
        const next: Draft = { ...EMPTY }
        next.receiptNo = s(row.rdnNo)
        hydrateFromRow(next, row, false)
        setDraft(next)
        applyDoDerived(s(row.doNo))
      }
      if (cancelled) return
      setFormLoading(false)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, recordId, row])

  const rdn = draft.receiptNo.trim()
  const rdnBlank = !rdn
  const rdnFormatBad = !!rdn && !rdn.toUpperCase().startsWith('RDN')
  const status = draft.deductionKg.trim() ? 'Received' : 'Estimated'

  const rdnHelper = rdnBlank
    ? 'Please enter valid RDN No!'
    : rdnFormatBad
      ? 'Wrong RDN No format!'
      : rdnExists
        ? 'RDN No existed!'
        : undefined

  const canSave = canEdit && !formLoading && !rdnBlank && !rdnFormatBad && (mode === 'edit' || !rdnExists) && !saving

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (!canSave) return
    const payload: ReceivedWrite = {
      rdnNo: draft.receiptNo.trim(),
      dateIn: draft.dateIn || null,
      doNo: draft.doNo || null,
      truckNo: draft.truckNo || null,
      fromSupplier: draft.supplierCode || null,
      toClient: draft.clientCode || null,
      warehouseCode: draft.warehouseCode || null,
      quality: draft.qualityCode || null,
      announcedQuantity: parseNum(draft.announcedQuantity),
      noOfBag: parseNum(draft.noOfBag),
      vgmQuantity: parseNum(draft.vgmQuantity),
      deductionKg: parseNum(draft.deductionKg),
      deductionVnd: parseNum(draft.deductionVnd),
      deductionOther: parseNum(draft.deductionOther),
      note: draft.note || null,
    }
    GRADE_KEYS.forEach((g) => { (payload as Record<string, unknown>)[g] = parseNum(draft[g]) })
    try {
      if (mode === 'edit' && recordId != null) {
        await updateReceived({ id: recordId, body: payload }).unwrap()
        snack.success(`Received ${payload.rdnNo} updated`)
      } else {
        await createReceived(payload).unwrap()
        snack.success(`Received ${payload.rdnNo} added`)
      }
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Save received failed'))
    }
  }

  const title = mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'
  const ro = !canEdit

  const dateField = (key: string, label: string) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      disabled={ro}
      value={draft[key] ? dayjs(draft[key]) : null}
      onChange={(d) => set(key, d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
      slotProps={{ textField: { size: SM, fullWidth: true, InputLabelProps: { shrink: true } } }}
    />
  )
  const numField = (key: string, label: string, decimals = true) => (
    <TextField
      label={label} size={SM} fullWidth type="text" value={draft[key]} disabled={ro}
      onChange={(e) => set(key, e.target.value)}
      onBlur={() => { if (decimals) set(key, fmtStd(draft[key])) }}
      inputProps={numProps}
    />
  )
  const textField = (key: string, label: string, extra?: object) => (
    <TextField label={label} size={SM} fullWidth value={draft[key]} disabled={ro}
      onChange={(e) => set(key, e.target.value)} {...extra} />
  )
  const roField = (key: string, label: string, numeric?: boolean) => (
    <TextField label={label} size={SM} fullWidth value={draft[key]}
      InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx}
      inputProps={numeric ? { style: { textAlign: 'right' } } : undefined} />
  )

  const rdnDisabled = ro || mode === 'edit' // PK locked on Edit

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" noWrap title={`${title} — Received`} sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700 }}>{title} — Received</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          {canEdit && (
            <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
              {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
            </Button>
          )}
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {formLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
          <>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit received records.</Alert>}

        {/* ── General ── (Access frmVirtualFactoryQryReceivedAdd, 2-column) */}
        <Typography sx={sectionTitleSx}>General</Typography>
        <Box sx={grid2Sx}>
          {/* Row 1: Date in | To Warehouse */}
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>{dateField('dateIn', 'Date in')}</Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 1 } }}>
            <Combo label="To Warehouse" value={draft.warehouseCode} valueLabel={labels.warehouseCode} disabled={ro} rawLabel
              options={warehouses.data?.items ?? []} loading={warehouses.isFetching} onInputChange={setWarehouseQ}
              onPick={(v, it) => { set('warehouseCode', v); setLabels((p) => ({ ...p, warehouseCode: it?.label ?? '' })) }} />
          </Box>
          {/* Row 2: RDN No | To Client */}
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 2 } }}>
            <TextField
              label="RDN No *" size={SM} fullWidth value={draft.receiptNo}
              disabled={rdnDisabled}
              error={!!rdnHelper}
              helperText={rdnHelper}
              onChange={(e) => { setRdnExists(false); set('receiptNo', e.target.value) }}
              onBlur={() => runCheck(draft.receiptNo)}
            />
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 2 } }}>
            <Combo label="To Client" value={draft.clientCode} valueLabel={labels.clientCode} disabled={ro} rawLabel
              options={clients.data?.items ?? []} loading={clients.isFetching} onInputChange={setClientQ}
              onPick={(v, it) => { set('clientCode', v); set('clientCodeRo', v); setLabels((p) => ({ ...p, clientCode: it?.label ?? '' })) }} />
          </Box>
          {/* Row 3: Truck No | To Client (Code) [ro] */}
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 3 } }}>{textField('truckNo', 'Truck No')}</Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 3 } }}>{roField('clientCodeRo', 'To Client (Code)')}</Box>
          {/* Row 4: From Supplier | No of bag */}
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 4 } }}>
            <Combo label="From Supplier" value={draft.supplierCode} valueLabel={labels.supplierCode} disabled={ro} rawLabel
              options={suppliers.data?.items ?? []} loading={suppliers.isFetching} onInputChange={setSupplierQ}
              onPick={(v, it) => { set('supplierCode', v); set('supplierCodeRo', v); setLabels((p) => ({ ...p, supplierCode: it?.label ?? '' })) }} />
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 4 } }}>{numField('noOfBag', 'No of bag', false)}</Box>
          {/* Row 5: From Supplier (Code) [ro] | Net Weight (KG) */}
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 5 } }}>{roField('supplierCodeRo', 'From Supplier (Code)')}</Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 5 } }}>{numField('vgmQuantity', 'Net Weight (KG)')}</Box>
          {/* Row 6: Quality | Announced Quantity */}
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 6 } }}>
            <Combo label="Quality" value={draft.qualityCode} disabled={ro}
              options={qualities.data?.items ?? []} loading={qualities.isFetching} onInputChange={setQualityQ}
              onPick={(v) => set('qualityCode', v)} />
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 6 } }}>{numField('announcedQuantity', 'Announced Quantity')}</Box>
        </Box>

        {/* ── Delivery Order ── (Access col1: DO No / S-Contract / Packaging; col2: Quality / Quantity) */}
        <Typography sx={sectionTitleSx}>Delivery Order</Typography>
        <Box sx={grid2Sx}>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>
            <Combo label="DO No" value={draft.doNo} disabled={ro}
              options={doNos.data?.items ?? []} loading={doNos.isFetching} onInputChange={setDoNoQ}
              onPick={(v) => { set('doNo', v); applyDoDerived(v) }} />
          </Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 2 } }}>{roField('doSaleContract', 'S - Contract')}</Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 2 } }}>{roField('doQuality', 'Quality')}</Box>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 3 } }}>{roField('doPackaging', 'Packaging')}</Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 3 } }}>{roField('doNetWeight', 'Quantity', true)}</Box>
        </Box>

        {/* ── Deduction ── (Access Label167 divider above grades; grades interleaved 2-col) */}
        <Typography sx={sectionTitleSx}>Deduction</Typography>
        <Box sx={grid2Sx}>
          {/* Grades: col1 = mc..foreign, col2 = insect..<S12 */}
          {GRADE_COL1.map((g, i) => (
            <Box key={g} sx={{ gridColumn: { md: 1 }, gridRow: { md: i + 1 } }}>{numField(g, GRADE_LABELS[g])}</Box>
          ))}
          {GRADE_COL2.map((g, i) => (
            <Box key={g} sx={{ gridColumn: { md: 2 }, gridRow: { md: i + 1 } }}>{numField(g, GRADE_LABELS[g])}</Box>
          ))}
          {/* Deduction (Kg) | Deduction (VNĐ) */}
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: GRADE_COL1.length + 1 } }}>{numField('deductionKg', 'Deduction (Kg)')}</Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: GRADE_COL1.length + 1 } }}>{numField('deductionVnd', 'Deduction (VNĐ)')}</Box>
          {/* Deduction Other | (blank) */}
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: GRADE_COL1.length + 2 } }}>{numField('deductionOther', 'Deduction Other')}</Box>
        </Box>

        {/* ── Summary (read-only) & Note ── (Access: Status | Payment Quantity, then Note) */}
        <Typography sx={sectionTitleSx}>Summary</Typography>
        <Box sx={grid2Sx}>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>
            <TextField label="Status" size={SM} fullWidth value={status}
              InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 1 } }}>{roField('paymentQuantity', 'Payment Quantity (KG)', true)}</Box>
          <Box sx={{ gridColumn: { md: '1 / -1' }, gridRow: { md: 2 } }}>
            {textField('note', 'Note', { multiline: true, minRows: 2 })}
          </Box>
        </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Copy scalar row fields into a draft (mode-agnostic; RDN handled by caller). */
function hydrateFromRow(next: Draft, row: ReceivedRow, isCopy: boolean) {
  next.dateIn = isCopy ? dayjs().format('YYYY-MM-DD') : s(row.dateIn).slice(0, 10)
  next.doNo = s(row.doNo)
  next.truckNo = s(row.truckNo)
  next.supplierCode = s(row.fromSupplier)
  next.clientCode = s(row.toClient)
  next.supplierCodeRo = s(row.fromSupplier)
  next.clientCodeRo = s(row.toClient)
  next.warehouseCode = s(row.warehouseCode)
  next.qualityCode = s(row.quality)
  next.announcedQuantity = fmtStd(row.announcedQuantity)
  next.noOfBag = s(row.noOfBag)
  next.vgmQuantity = fmtStd(row.vgmQuantity)
  next.deductionKg = fmtStd(row.deductionKg)
  next.deductionVnd = fmtStd(row.deductionVnd)
  next.deductionOther = fmtStd(row.deductionOther)
  next.note = s(row.note)
  next.paymentQuantity = isCopy ? '' : fmtStd(row.paymentQuantity)
  GRADE_KEYS.forEach((g) => { next[g] = fmtStd((row as unknown as Record<string, unknown>)[g]) })
}
